import React, { useState } from 'react';
import './CitationModal.css';

const CitationModal = ({ paper, onClose }) => {
  const [selectedFormat, setSelectedFormat] = useState('APA');
  const [copied, setCopied] = useState(false);

  // Format authors for different citation styles
  const formatAuthorsAPA = (authors) => {
    if (!authors || authors.length === 0) return '';

    const authorNames = authors.map(a => {
      const name = typeof a === 'object' ? a.name : a;
      const parts = name.split(' ');
      if (parts.length >= 2) {
        const lastName = parts[parts.length - 1];
        const initials = parts.slice(0, -1).map(n => n[0] + '.').join(' ');
        return `${lastName}, ${initials}`;
      }
      return name;
    });

    if (authorNames.length === 1) return authorNames[0];
    if (authorNames.length === 2) return authorNames.join(' & ');
    if (authorNames.length > 2) {
      return authorNames.slice(0, -1).join(', ') + ', & ' + authorNames[authorNames.length - 1];
    }
  };

  const formatAuthorsMLA = (authors) => {
    if (!authors || authors.length === 0) return '';

    const authorNames = authors.map(a => {
      const name = typeof a === 'object' ? a.name : a;
      const parts = name.split(' ');
      if (parts.length >= 2) {
        const lastName = parts[parts.length - 1];
        const firstName = parts.slice(0, -1).join(' ');
        return `${lastName}, ${firstName}`;
      }
      return name;
    });

    if (authorNames.length === 1) return authorNames[0];
    if (authorNames.length === 2) return authorNames.join(', and ');
    if (authorNames.length === 3) return authorNames.join(', ');
    if (authorNames.length > 3) return authorNames[0] + ', et al';
  };

  const formatAuthorsChicago = (authors) => {
    if (!authors || authors.length === 0) return '';

    const authorNames = authors.map(a => {
      const name = typeof a === 'object' ? a.name : a;
      const parts = name.split(' ');
      if (parts.length >= 2) {
        const lastName = parts[parts.length - 1];
        const firstName = parts.slice(0, -1).join(' ');
        return `${lastName}, ${firstName}`;
      }
      return name;
    });

    if (authorNames.length === 1) return authorNames[0];
    if (authorNames.length === 2) return authorNames.join(' and ');
    if (authorNames.length > 2) {
      return authorNames.slice(0, -1).join(', ') + ', and ' + authorNames[authorNames.length - 1];
    }
  };

  const formatAuthorsHarvard = (authors) => {
    if (!authors || authors.length === 0) return '';

    const authorNames = authors.map(a => {
      const name = typeof a === 'object' ? a.name : a;
      const parts = name.split(' ');
      if (parts.length >= 2) {
        const lastName = parts[parts.length - 1];
        const initials = parts.slice(0, -1).map(n => n[0].toUpperCase()).join('');
        return `${lastName}, ${initials}.`;
      }
      return name;
    });

    if (authorNames.length === 1) return authorNames[0];
    if (authorNames.length === 2) return authorNames.join(' & ');
    if (authorNames.length > 2) {
      return authorNames.slice(0, -1).join(', ') + ' & ' + authorNames[authorNames.length - 1];
    }
  };

  const formatAuthorsVancouver = (authors) => {
    if (!authors || authors.length === 0) return '';

    const authorNames = authors.map(a => {
      const name = typeof a === 'object' ? a.name : a;
      const parts = name.split(' ');
      if (parts.length >= 2) {
        const lastName = parts[parts.length - 1];
        const initials = parts.slice(0, -1).map(n => n[0].toUpperCase()).join('');
        return `${lastName} ${initials}`;
      }
      return name;
    });

    if (authorNames.length <= 6) {
      return authorNames.join(', ');
    } else {
      return authorNames.slice(0, 3).join(', ') + ', et al';
    }
  };

  const generateCitation = () => {
    const year = paper.year || new Date().getFullYear();
    const title = paper.title || 'Untitled';
    const journal = 'Federal University Wukari Journal of Agriculture and Life Sciences';
    const volume = paper.volume || '';
    const issue = paper.issue || '';
    const pages = paper.pages || '';
    const doi = paper.doi || '';

    switch (selectedFormat) {
      case 'APA':
        return `${formatAuthorsAPA(paper.authors)} (${year}). ${title}. ${journal}${volume ? `, ${volume}` : ''}${issue ? `(${issue})` : ''}${pages ? `, ${pages}` : ''}.${doi ? ` https://doi.org/${doi}` : ''}`;

      case 'MLA':
        return `${formatAuthorsMLA(paper.authors)}. "${title}." ${journal}${volume ? ` ${volume}` : ''}${issue ? `.${issue}` : ''} (${year})${pages ? `: ${pages}` : ''}. Print.`;

      case 'Chicago':
        return `${formatAuthorsChicago(paper.authors)}. "${title}." ${journal}${volume ? ` ${volume}` : ''}${issue ? `, no. ${issue}` : ''} (${year})${pages ? `: ${pages}` : ''}.${doi ? ` https://doi.org/${doi}` : ''}`;

      case 'Harvard':
        return `${formatAuthorsHarvard(paper.authors)} ${year}, '${title}', ${journal}${volume ? `, vol. ${volume}` : ''}${issue ? `, no. ${issue}` : ''}${pages ? `, pp. ${pages}` : ''}.${doi ? ` DOI: ${doi}` : ''}`;

      case 'Vancouver':
        return `${formatAuthorsVancouver(paper.authors)}. ${title}. ${journal}. ${year}${volume ? `;${volume}` : ''}${issue ? `(${issue})` : ''}${pages ? `:${pages}` : ''}.`;

      case 'IEEE':
        const ieeeAuthors = paper.authors?.map(a => {
          const name = typeof a === 'object' ? a.name : a;
          const parts = name.split(' ');
          if (parts.length >= 2) {
            const lastName = parts[parts.length - 1];
            const initials = parts.slice(0, -1).map(n => n[0].toUpperCase() + '.').join(' ');
            return `${initials} ${lastName}`;
          }
          return name;
        }).join(', ');
        return `${ieeeAuthors}, "${title}," ${journal}${volume ? `, vol. ${volume}` : ''}${issue ? `, no. ${issue}` : ''}${pages ? `, pp. ${pages}` : ''}, ${year}.${doi ? ` doi: ${doi}` : ''}`;

      case 'BibTeX':
        const bibtexKey = `${paper.authors?.[0]?.name?.split(' ').pop() || 'author'}${year}`;
        return `@article{${bibtexKey},
  title={${title}},
  author={${paper.authors?.map(a => typeof a === 'object' ? a.name : a).join(' and ')}},
  journal={${journal}},
  ${volume ? `volume={${volume}},\n  ` : ''}${issue ? `number={${issue}},\n  ` : ''}${pages ? `pages={${pages}},\n  ` : ''}year={${year}}${doi ? `,\n  doi={${doi}}` : ''}
}`;

      default:
        return '';
    }
  };

  const handleCopy = () => {
    const citation = generateCitation();
    navigator.clipboard.writeText(citation).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    const citation = generateCitation();
    const blob = new Blob([citation], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `citation-${selectedFormat.toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  return (
    <div className="citation-modal-overlay" onClick={onClose}>
      <div className="citation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="citation-modal-header">
          <h3>Cite This Paper</h3>
          <button className="citation-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="citation-modal-body">
          <div className="citation-format-selector">
            <label>Citation Format:</label>
            <div className="citation-format-buttons">
              {['APA', 'MLA', 'Chicago', 'Harvard', 'Vancouver', 'IEEE', 'BibTeX'].map(format => (
                <button
                  key={format}
                  className={`format-btn ${selectedFormat === format ? 'active' : ''}`}
                  onClick={() => setSelectedFormat(format)}
                >
                  {format}
                </button>
              ))}
            </div>
          </div>

          <div className="citation-preview">
            <label>Citation:</label>
            <div className="citation-text">
              <pre>{generateCitation()}</pre>
            </div>
          </div>

          <div className="citation-actions">
            <button className="btn btn-primary" onClick={handleCopy}>
              <i className={`fas fa-${copied ? 'check' : 'copy'} me-2`}></i>
              {copied ? 'Copied!' : 'Copy Citation'}
            </button>
            <button className="btn btn-secondary" onClick={handleDownload}>
              <i className="fas fa-download me-2"></i>
              Download
            </button>
          </div>

          <div className="citation-info">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Select your preferred citation format above. You can copy the citation to your clipboard or download it as a text file.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitationModal;